/**
 * PHARMAKON — route access (design/ux/01_foundations.md §6.6, WP-18;
 * AUD-NAV-04, AUD-NAV-11).
 *
 * ONE table of « who may open which page », read by BOTH the router guard
 * (`App.tsx` `<RequireAuth route="/achats">`) and the menu filter
 * (`lib/nav-tree.js` `selectNavForUser`). The arrays were moved here
 * one-for-one from the `roles={[…]}` props of App.tsx (same values, same
 * order): before, a menu entry could offer a page its guard refused and the
 * user was bounced silently (Paramètres for MANAGER, AUD-NAV-04).
 *
 * Keys are react-router patterns of the pharmacy shell (absolute, `:param`
 * segments). `ANY_AUTHENTICATED` (null) = every signed-in user. A pattern
 * that is not listed is refused (fail closed); the navigation unit test
 * asserts that every guarded route of App.tsx is listed here.
 *
 * The fournisseur portal (`/portail-fournisseur/*`, SUPPLIER_ROLES) keeps its
 * own route tree and guard (A-20) and is not part of this table.
 */

/** Pharmacy-plane roles (Prisma `RoleType`, schema.prisma:33). */
export const ROLES = Object.freeze({
  ADMIN_PHARMAKON: "ADMIN_PHARMAKON",
  PROPRIETAIRE: "PROPRIETAIRE",
  MANAGER: "MANAGER",
  CAISSIER: "CAISSIER",
});

/** Guard value of a route open to every signed-in user. */
export const ANY_AUTHENTICATED = null;

const P = ROLES.PROPRIETAIRE;
const M = ROLES.MANAGER;
const C = ROLES.CAISSIER;
const A = ROLES.ADMIN_PHARMAKON;
const r = (...roles) => Object.freeze(roles);

/** @type {Readonly<Record<string, ReadonlyArray<string> | null>>} */
export const ROUTE_ACCESS = Object.freeze({
  "/dashboard": ANY_AUTHENTICATED,
  "/caissier": r(C, P, M),
  "/equipe": r(P, M),
  "/medecins": r(P, M),
  "/ordonnancier": r(P, M),
  "/ordonnancier/annexe-12": r(P, M),
  "/ordonnancier/annexe-13": r(P, M),
  // X XWP-55 — the delivery drawer (ordonnancier:read P / M here; its attachments P / M, never the caissier)
  "/ordonnancier/:id": r(P, M),
  "/secours": r(P, M),
  "/manquants": r(P, M),
  "/manquants/assistant-commande": r(P, M),
  "/stock/risque-peremption": r(P, M),
  "/fidelite": r(P, M),
  "/facturation": r(P, M),
  "/tresorerie": r(P, M),
  "/caisse-centrale": r(P, M),
  // Trésorerie TWP-18 — « Comptes & caisses » and its « Sessions de caisse » tab (tresorerie_pharmacie:read).
  "/tresorerie/comptes": r(P, M),
  "/tresorerie/comptes/sessions": r(P, M),
  // Trésorerie TWP-20 — « Mouvements » (the journal of operations; tresorerie_pharmacie:read).
  "/tresorerie/mouvements": r(P, M),
  // Trésorerie TWP-26 — « Règlements » (reglement_achat:read — P, M; the caissier never, RC-48).
  "/tresorerie/reglements": r(P, M),
  "/tresorerie/reglements/:onglet": r(P, M),
  // Trésorerie TWP-23 — « Paramètres de trésorerie », the WP-20 workspace section (P edits, M reads — TD-152).
  "/tresorerie/parametres": r(P, M),
  "/tresorerie/parametres/:onglet": r(P, M),
  "/ma-tresorerie": r(C, P, M),
  "/rapports-finance": r(P, M),
  "/magasins": r(P, M),
  "/confreres": r(P, M),
  "/support": ANY_AUTHENTICATED,
  "/sync/a-verifier": r(P, M),
  "/audit": r(P, M),
  "/abonnement": r(P, M),
  "/moncompte": ANY_AUTHENTICATED,
  "/paiement/succes": ANY_AUTHENTICATED,
  "/paiement/echec": ANY_AUTHENTICATED,
  "/caisse": r(P, M, C),
  "/chifa": r(P, M, C),
  "/ventes": r(P, M, C),
  "/achats": r(P, M),
  "/achats/nouveau": r(P, M),
  "/achats/journal-detaille": r(P, M), // refonte WP-52 (S5, achat:read)
  // Refonte WP-31 — bons de retour fournisseur (bon_retour:read, A-07: P + M).
  "/achats/bons-retour": r(P, M),
  "/achats/bons-retour/nouveau": r(P, M),
  "/achats/bons-retour/:id": r(P, M),
  // Refonte WP-35 — réclamations fournisseur (reclamation:read, A-07 / A-61 (1): P + M).
  "/achats/reclamations": r(P, M),
  "/achats/reclamations/nouvelle": r(P, M),
  "/achats/reclamations/:id": r(P, M),
  "/achats/:id/modifier": r(P, M),
  "/achats/:id": r(P, M),
  "/efactures-recu": r(P, M),
  "/efactures-recu/:id": r(P, M),
  "/achat/urgence": r(P, M),
  "/achat/efacture/:id/workspace": r(P, M),
  "/avoirs": r(P, M),
  "/avoirs/nouveau": r(P, M),
  "/avoirs/:id": r(P, M),
  "/stock": r(P, M, C),
  "/stock/corrections": r(P, M),
  "/recherche-produits": r(P, M, C),
  "/emplacements": r(P, M),
  "/produits": r(P, M),
  "/produits/nouveau": r(P, M),
  "/produits/:id": r(P, M),
  "/fournisseurs": r(P, M),
  "/fournisseurs/nouveau": r(P, M),
  "/fournisseurs/:id": r(P, M),
  "/parametres": r(P),
  // WP-20 — the sections and onglets of the one settings workspace (config/parametres.config.js).
  "/parametres/:section": r(P),
  "/parametres/:section/:onglet": r(P),
  "/admin": r(A),
  "/admin/dashboard": r(A),
  "/admin/pharmacies": r(A),
  "/admin/pharmacies/:id/licences": r(A),
  "/admin/fournisseurs": r(A),
  "/admin/verticals/novanalyse": r(A),
  "/admin/verticals/cabinets": r(A),
  "/admin/verticals/ailaji": r(A),
  "/admin/licences": r(A),
  "/admin/billing": r(A),
  "/admin/abonnements-fournisseurs": r(A),
  "/admin/pricing": r(A),
  "/admin/modules": r(A),
  "/admin/cnas": r(A),
  "/admin/cnas/import": r(A),
  "/admin/monitor/health": r(A),
  "/admin/monitor/audit": r(A),
  "/admin/monitor/errors": r(A),
  "/admin/monitor/users": r(A),
  "/admin/monitor/security": r(A),
  "/admin/notifications": r(A),
  "/admin/broadcasts": r(A),
  "/admin/tickets": r(A),
  "/decision": r(P, M),
  "/decision/besoin": r(P, M),
  "/decision/offres": r(P, M),
  "/decision/offres/historique": r(P, M),
  "/aide-decision/config": r(P, M),
  "/aide-decision/pre-commandes/:id/comparison": r(P, M),
  "/aide-decision/offres/:id/counter": r(P, M),
  "/commercial/catalogue": r(P, M),
  "/commercial/appel-offres": r(P, M),
  // X XWP-76 (CP §15.9) — the appel's detail page (same screen as /comparaison).
  "/commercial/appel-offres/:id": r(P, M),
  "/commercial/appel-offres/:id/comparaison": r(P, M),
  "/commercial/bons-commande": r(P, M),
  "/commercial/bons-commande/nouveau": r(P, M),
  "/commercial/bons-commande/:id": r(P, M),
  "/commercial/precommandes-a-confirmer": r(P, M),
  "/commercial/achat-urgent": r(P, M),
  "/commercial/reapprovisionnement": r(P, M),
  "/livraisons/incoming": r(P, M),
  "/conseil": r(P, M, C),
  "/dermo-conseiller": r(P, M, C),
  "/admin/produits-base": r(A),
  "/admin/produits-base/import": r(A),
  "/admin/aalaji/referentiels": r(A),
  "/aalaji": r(P, M, C),
  "/aalaji/preparations": r(P, M, C),
  "/aalaji/parametres": r(P, M),
  "/clients": r(P, M, C, A),
  "/clients/nouveau": r(P, M, C, A),
  "/clients/:id/edit": r(P, M, A),
  "/clients/:id": r(P, M, C, A),
  "/credits": r(P, M, C, A),
  // X XWP-20 — the journal with a crédit's drawer open.
  "/credits/:id": r(P, M, C, A),
  "/instances-chifa": r(P, M, C),
  "/instances-chifa/nouveau": r(P, M, C),
  // X XWP-21 — the journal with an instance's drawer open.
  "/instances-chifa/:id": r(P, M, C),
  "/retours": r(P, M, C),
  "/retours/nouveau": r(P, M, C),
  "/retours/:id": r(P, M, C),
  "/decision/etat-rotation": r(P, M),
  "/analytics": r(P, M),
  "/analytics/medecins/:id": r(P, M),
});

/**
 * Legacy URLs kept alive as client-side redirects (ux/01 §6.13): bookmarks
 * and server deep links keep working; the query string is carried over.
 * They have no guard of their own — the target's guard applies.
 */
export const ROUTE_REDIRECTS = Object.freeze({
  "/stock/produit/nouveau": "/produits/nouveau",
  "/instances": "/credits",
  // AUD-NAV-17 — duplicate of /moncompte (same component).
  "/mon-compte": "/moncompte",
  // Trésorerie TWP-18 (ADR-12, TD-59) — the sessions list is a tab of « Comptes & caisses ».
  // /caisse-centrale stays live until TWP-27 / TWP-29 replace its Journal and Charges tabs (TD-254).
  "/caisses": "/tresorerie/comptes/sessions",
  // Trésorerie TWP-28 (TO-02, TD-51) — the refonte WP-53 URL lands on the one settlement page;
  // `?tab=` / `?fournisseur=` are rewritten on the way (lib/tresorerie/reglements.js REDIRECTIONS_REGLEMENTS).
  "/achats/reglements": "/tresorerie/reglements",
  // TD-131 — « Mon relevé » mixed every fournisseur (RC-115): the relevé is the fournisseur's drawer,
  // reached from « Soldes fournisseurs » (the rewriter lands on that tab); `?fournisseur_id=` opens it on « Relevé ».
  "/mon-releve": "/tresorerie/reglements",
  // X XWP-64 (CP §15.2, AUD-NAV-17, XD-256) — the legacy audit page read the same GET /audit without the
  // descriptions: one journal, /audit (P / M, unchanged); filters in the query string are kept.
  "/admin/audit": "/audit",
  // X XWP-68 (CP §15.3, XMD-CP-20) — the legacy user page managed the same users as /equipe with another
  // component: one team workspace, /equipe (P; MANAGER read-only), also Paramètres › Utilisateurs.
  "/admin/utilisateurs": "/equipe",
});

/** True when `pattern` is a route of the table. */
export function isKnownRoute(pattern) {
  return Object.prototype.hasOwnProperty.call(ROUTE_ACCESS, pattern);
}

/**
 * The roles a route admits, as `<RequireAuth roles>` expects them:
 * `undefined` = any signed-in user; an unknown pattern admits nobody.
 * @param {string} pattern
 * @returns {ReadonlyArray<string> | undefined}
 */
export function routeRoles(pattern) {
  if (!isKnownRoute(pattern)) return Object.freeze([]);
  const roles = ROUTE_ACCESS[pattern];
  return roles === ANY_AUTHENTICATED ? undefined : roles;
}

/**
 * Whether `role` may open the route `pattern` (fail closed on an unknown
 * pattern or a missing role).
 * @param {string | undefined | null} role
 * @param {string} pattern
 */
export function canAccessRoute(role, pattern) {
  if (!role || !isKnownRoute(pattern)) return false;
  const roles = ROUTE_ACCESS[pattern];
  return roles === ANY_AUTHENTICATED || roles.includes(role);
}
